import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { catchError, EMPTY, finalize, map, tap } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AccountBackend } from '@libs/src/account/account.backend';
import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { GroupsBackend } from '@libs/src/groups/groups.backend';
import { ToastService } from '@libs/src/toast/toast.service';

import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import { Account } from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import {
  ProgramAccount,
  ProgramMembership,
  ProgramRole,
} from '@libs/src/models/program-membership.model';
import { Program } from '@libs/src/models/program.model';
import { canUserManageProgram } from '@main-client/src/app/user/user.helpers';

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-program-participant-preferences-edit-drawer',
  templateUrl: './program-participant-preferences-edit-drawer.component.html',
})
export class ProgramParticipantPreferencesEditDrawerComponent {
  @Input() group: Group;
  @Input() program: Program;
  @Input() programMembership: ProgramMembership;
  @Input() programAccounts: ProgramAccount[];
  @Input() user: Account;
  programPreferencesFormControl = new FormControl();
  isLoading = false;

  constructor(
    private readonly accountBackend: AccountBackend,
    private readonly destroy$: DestroySubscriptionsService,
    public readonly drawerRef: NzDrawerRef<ProgramParticipantPreferencesEditDrawerComponent>,
    private readonly groupsBackend: GroupsBackend,
    private readonly toastService: ToastService,
  ) {}

  saveParticipantPreferences() {
    if (!this.programPreferencesFormControl.valid) {
      return;
    }
    this.isLoading = true;
    const { capacity, preferred_matches, roles } =
      this.programPreferencesFormControl.value;
    const program_preferences = {
      capacity,
      preferred_matches,
      program_roles: (roles?.split(',') as ProgramRole[]) ?? [],
    };
    const canUpdateGroupProgramMemberships = canUserManageProgram(
      this.user,
      this.group,
      this.program,
    );
    const updateProgram$ = canUpdateGroupProgramMemberships
      ? this.groupsBackend.v3
          .patchGroupProgramMemberships(
            this.group._id,
            this.program._id,
            [this.programMembership._id],
            { program_preferences },
          )
          .pipe(
            map(({ programMemberships }) => ({
              programMembership: programMemberships[0],
            })),
          )
      : this.accountBackend.v3.patchAccountGroupsProgramMembershipById(
          this.group,
          this.program._id,
          { program_preferences },
        );
    updateProgram$
      .pipe(
        tap(({ programMembership }) => {
          const successMessage = $localize`:@@groupProgramPreferences.preferencesUpdatedMessage:Preferences updated!`;
          this.drawerRef.close({ programMembership });
          this.toastService.showSuccess(successMessage);
        }),
        catchError((response: HttpErrorResponse) => {
          this.toastService.showError(getServerError(response));
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
